// import { API_BASE } from "./lib/utils";

import { API_BASE } from "./utils";
import { getAuthHeaders } from "./auth-fetch";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";



export const BADGE_PRESETS = [
  { id: "badge_a6_portrait", label: "Badge A6 Portrait", widthMm: 105, heightMm: 148 },
  { id: "badge_a7_landscape", label: "Badge A7 Landscape", widthMm: 105, heightMm: 74 },
  { id: "card_cr80", label: "Card CR80", widthMm: 85.6, heightMm: 54 },
  { id: "ticket_event", label: "Event Ticket", widthMm: 180, heightMm: 70 },
  { id: "wristband", label: "Wristband", widthMm: 250, heightMm: 25 },
  { id: "custom", label: "Custom Size", widthMm: 105, heightMm: 148 },
];

export const LAYER_TYPES = [
  { id: "text", label: "Text" },
  { id: "image", label: "Image" },
  { id: "qr", label: "QR" },
  { id: "shape", label: "Shape" },
];

/** Pixel layout for A6 portrait canvas (~441 × 622px at 4.2px/mm). */
export const DEFAULT_BADGE_TEMPLATE_LAYERS = [
  { id: "l_event", type: "text", x: 32, y: 36, w: 376, h: 22, content: "{{event.title}}", fontSize: 10, fontWeight: 700 },
  { id: "l_name", type: "text", x: 32, y: 72, w: 376, h: 84, content: "{{attendee.name}}", fontSize: 36, fontWeight: 700 },
  { id: "l_accent", type: "shape", x: 32, y: 168, w: 72, h: 5, content: "", fill: "var(--primary)", textColor: "var(--primary)" },
  { id: "l_company", type: "text", x: 32, y: 188, w: 376, h: 32, content: "{{attendee.company}}", fontSize: 16, fontWeight: 600 },
  { id: "l_role", type: "text", x: 32, y: 224, w: 376, h: 26, content: "{{attendee.role}}", fontSize: 13, fontWeight: 500 },
  { id: "l_ticket", type: "shape", x: 32, y: 272, w: 152, h: 34, content: "{{attendee.ticket}}", fontSize: 11, fontWeight: 800, fill: "#09090B", textColor: "var(--primary)" },
  { id: "l_qr", type: "qr", x: 281, y: 430, w: 128, h: 128, content: "{{qr.code}}" },
];

export const DEFAULT_BADGE_TEMPLATE = {
  name: "Default Badge",
  preset: "badge_a6_portrait",
  size: { unit: "mm", width: 105, height: 148 },
  layers: DEFAULT_BADGE_TEMPLATE_LAYERS,
  variants: { attendee: true, staff: true, vip: true },
  logoUrl: "",
};

const LEGACY_BADGE_LAYER_IDS = new Set(["l_logo", "l_name", "l_company", "l_role", "l_qr", "l_ticket"]);

export function isLegacyBadgeTemplate(template) {
  const layers = Array.isArray(template?.layers) ? template.layers : [];
  if (!layers.length) return false;
  const hasLegacyIds = layers.some((layer) => LEGACY_BADGE_LAYER_IDS.has(layer?.id));
  if (!hasLegacyIds) return false;
  const maxDim = layers.reduce(
    (max, layer) => Math.max(max, Number(layer?.x) || 0, Number(layer?.y) || 0, Number(layer?.w) || 0, Number(layer?.h) || 0),
    0,
  );
  return maxDim <= 120;
}

export function upgradeLegacyBadgeTemplate(template) {
  return {
    ...DEFAULT_BADGE_TEMPLATE,
    name: template?.name || DEFAULT_BADGE_TEMPLATE.name,
    logoUrl: template?.logoUrl || "",
    variants: template?.variants || DEFAULT_BADGE_TEMPLATE.variants,
  };
}

export function normalizeBadgeTemplate(template) {
  const base = template && typeof template === "object" ? template : {};
  const upgraded = isLegacyBadgeTemplate(base) ? upgradeLegacyBadgeTemplate(base) : base;
  return {
    name: upgraded?.name || DEFAULT_BADGE_TEMPLATE.name,
    preset: upgraded?.preset || DEFAULT_BADGE_TEMPLATE.preset,
    size: upgraded?.size || DEFAULT_BADGE_TEMPLATE.size,
    layers: normalizeBadgeLayers(Array.isArray(upgraded?.layers) ? upgraded.layers : DEFAULT_BADGE_TEMPLATE_LAYERS),
    variants: upgraded?.variants || DEFAULT_BADGE_TEMPLATE.variants,
    logoUrl: upgraded?.logoUrl || "",
  };
}

export const BADGE_STARTER_TEMPLATES = [
  {
    id: "minimal_name_qr",
    label: "Minimal Name + QR",
    description: "Fast check-in layout with bold name and QR.",
    recommendedPreset: "badge_a6_portrait",
    preview: { title: "NAME", strip: "STANDARD", qr: true },
    layers: [
      { type: "text", x: 32, y: 56, w: 376, h: 72, content: "{{attendee.name}}", fontSize: 34, fontWeight: 700 },
      { type: "shape", x: 32, y: 148, w: 140, h: 32, content: "{{attendee.ticket}}", fontSize: 11, fontWeight: 800, fill: "#09090B", textColor: "var(--primary)" },
      { type: "qr", x: 32, y: 430, w: 128, h: 128, content: "{{qr.code}}" },
    ],
  },
  {
    id: "conference_standard",
    label: "Conference Standard",
    description: "Event title, name, company, role and scannable QR.",
    recommendedPreset: "badge_a6_portrait",
    preview: { title: "NAME", sub: "COMPANY · ROLE", strip: "ATTENDEE", qr: true },
    layers: DEFAULT_BADGE_TEMPLATE_LAYERS,
  },
  {
    id: "vip_speaker",
    label: "VIP / Speaker",
    description: "Prominent role ribbon for VIP and speaker flows.",
    recommendedPreset: "badge_a6_portrait",
    preview: { title: "SPEAKER", strip: "VIP", qr: true },
    layers: [
      { type: "shape", x: 32, y: 48, w: 88, h: 30, content: "SPEAKER", fontSize: 11, fontWeight: 800, fill: "#09090B", textColor: "var(--primary)" },
      { type: "text", x: 32, y: 96, w: 376, h: 80, content: "{{vip.name}}", fontSize: 34, fontWeight: 700 },
      { type: "text", x: 32, y: 184, w: 376, h: 32, content: "{{vip.title}}", fontSize: 15, fontWeight: 600 },
      { type: "qr", x: 281, y: 430, w: 128, h: 128, content: "{{qr.code}}" },
    ],
  },
  {
    id: "blank_custom",
    label: "Blank Custom",
    description: "Start from scratch with an empty canvas.",
    recommendedPreset: "custom",
    preview: { title: "BLANK", sub: "Custom build" },
    layers: [],
  },
];

export function normalizeBadgeLayer(layer, idx = 0) {
  const type = layer?.type || "text";
  const defaults = {
    text: { w: 200, h: 34, fontSize: 16 },
    image: { w: 220, h: 120, fontSize: 16 },
    qr: { w: 120, h: 120, fontSize: 16 },
    shape: { w: 180, h: 42, fontSize: 14 },
  }[type] || { w: 200, h: 34, fontSize: 16 };
  return {
    id: layer?.id || `l_${Date.now()}_${idx}`,
    type,
    x: Number.isFinite(Number(layer?.x)) ? Number(layer.x) : 16,
    y: Number.isFinite(Number(layer?.y)) ? Number(layer.y) : 16,
    w: Number.isFinite(Number(layer?.w)) ? Number(layer.w) : defaults.w,
    h: Number.isFinite(Number(layer?.h)) ? Number(layer.h) : defaults.h,
    content: layer?.content ?? "",
    fontSize: Number.isFinite(Number(layer?.fontSize)) ? Number(layer.fontSize) : defaults.fontSize,
    fontWeight: Number.isFinite(Number(layer?.fontWeight)) ? Number(layer.fontWeight) : 600,
    fill: layer?.fill ?? "#09090B",
    textColor: layer?.textColor ?? "var(--primary)",
    locked: Boolean(layer?.locked),
    hidden: Boolean(layer?.hidden),
  };
}

export function normalizeBadgeLayers(layers = []) {
  const seen = new Set();
  return (Array.isArray(layers) ? layers : []).map((layer, idx) => {
    const next = normalizeBadgeLayer(layer, idx);
    while (seen.has(next.id)) {
      next.id = `l_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 6)}`;
    }
    seen.add(next.id);
    return next;
  });
}

export function materializeBadgeLayers(layers = []) {
  return normalizeBadgeLayers(layers);
}

export async function fetchBadgeTemplate(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/badge-template`, {
    headers: { ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error("Could not load badge template");
  const payload = await res.json();
  return payload.value;
}

export async function saveBadgeTemplate(eventId, value) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/badge-template`, {
    method: "PUT",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(value),
  });
  if (!res.ok) throw new Error("Could not save badge template");
  const payload = await res.json();
  return payload.value;
}

export async function fetchBadgeDataSources(eventId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/badge-data-sources`, {
    headers: { ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error("Could not load badge data sources");
  const payload = await res.json();
  return payload.data;
}

export async function encodeBadgeQr(eventId, subjectType, subjectId) {
  const res = await fetch(`${API_BASE}/api/events/${eventId}/badge/encode-qr`, {
    method: "POST",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ subjectType, subjectId }),
  });
  if (!res.ok) throw new Error("Could not encode QR");
  const payload = await res.json();
  return payload.code;
}
